import React, { useEffect, useState } from 'react';
/**
 * `LateRenewalAcceptance` presents additional acceptance conditions when a
 * renewal is considered "late".
 *
 * Behaviour:
 * - Selects the appropriate conditions list based on journey type (call centre
 *   vs standard renewals).
 * - Renders each condition as a checkbox and disables Continue until all are
 *   checked.
 * - Preserves and forwards the existing router `state` to the next step.
 */

import { useNavigate, useLocation } from 'react-router-dom';
import {
  Journey,
  LateRenewalCallCenterConditions,
  LateRenewalConditions,
  PageRelativePath
} from '../../../consts/Common';
import Checkbox from '../../common/Checkbox/Checkbox';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../app-state/Hooks';

const LateRenewalAcceptance = (): JSX.Element => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const journeyType = useAppSelector((s) => s.appConfig.journeyType);
  const { t } = useTranslation();

  const lateRenewalConditions =
    journeyType === Journey.CallCentre
      ? LateRenewalCallCenterConditions
      : LateRenewalConditions;

  const [conditionStates, setConditionStates] = useState<boolean[]>([]);

  useEffect(() => {
    const initialConditionStates: boolean[] = [];
    lateRenewalConditions.forEach(() => initialConditionStates.push(false));
    setConditionStates(initialConditionStates);

    window.scrollTo(0, 0);
  }, []);

  const onChange = (index: number) => {
    const updatedConditionStates = conditionStates.map((conditionState, i) =>
      i === index ? !conditionState : conditionState
    );

    setConditionStates(updatedConditionStates);
  };

  const onNext = () => {
    if (state) {
      navigate(getNextPage(), { state });
    }
  };

  const getNextPage = () => {
    const result =
      journeyType === Journey.CallCentre
        ? PageRelativePath.VehicleDetails
        : PageRelativePath.PersonalDetails;

    return result;
  };

  const disableContinue = conditionStates.some((v) => v === false);

  const callCentreExpiryHeading = t('landing-page.call-centre-expiry-heading');
  const callCentreExpiryMessage = t('landing-page.call-centre-expiry-message');
  const callCentreExpiryInstructions = t(
    'landing-page.call-centre-expiry-instructions'
  );
  const callCentrePriceChangeMessage = t(
    'landing-page.call-centre-price-change-message'
  );

  return (
    <div>
      {journeyType !== Journey.CallCentre && (
        <>
          <h3>{callCentreExpiryHeading}</h3>
          <p className='p2'>{callCentreExpiryInstructions}</p>
        </>
      )}

      {journeyType === Journey.CallCentre && (
        <p className='p2 m1-top'>{callCentreExpiryMessage}</p>
      )}

      <div className='form form-left-border'>
        {lateRenewalConditions.map((condition, i) => (
          <Checkbox
            key={`condtion-control-${i}`}
            id={`condition-${i}`}
            checked={conditionStates[i] || false}
            text={condition}
            onChange={() => onChange(i)}
          />
        ))}
      </div>
      {journeyType !== Journey.CallCentre && (
        <p>{callCentrePriceChangeMessage}</p>
      )}
      <div className='flex-row form-nav mobile-width-90pc'>
        <ButtonContinue
          name='btn-continue'
          text={t('landing-page.button-continue-text')}
          onClick={onNext}
          disabled={disableContinue}
        />
      </div>
    </div>
  );
};

export default LateRenewalAcceptance;
